import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import PageContainer from '../layout/PageContainer';

// Constants for consistent styling and content
const STYLES = {
    container: {
        minHeight: '100vh',
        bgcolor: 'rgba(18,18,18,1)',
        pt: { xs: 8, md: 4 },
        pb: 8
    },
    contentWrapper: {
        maxWidth: '1200px',
        mx: 'auto'
    },
    contentSection: {
        px: { xs:1.5, sm: 2 },
        pt: 0,
        pb: 4,
    },
    stackContainer: {
        px: { xs: 0, sm: 3 }
    },
    mainTitle: {
        color: 'white',
        fontSize: { xs: '24px', md: '30px', lg: '53px' },
        fontWeight: 700,
        textAlign: 'center',
        mb: 3,
        fontFamily: '"Wix Madefor Display", sans-serif',
    },
    sectionTitle: {
        color: 'rgba(198, 203, 212, 1)',
        fontSize: { xs: '14px', sm: '18px', lg: '32px' },
        fontWeight: 400,
        fontFamily: '"Inter", sans-serif',
        mt: 2,
    },
    bodyText: {
        color: 'rgba(198, 203, 212, 1)',
        fontSize: { xs: '14px', sm: '18px', lg: '32px' },
        lineHeight: { xs: '160%', lg: '38px' },
        opacity: 0.9,
        fontFamily: '"Inter", sans-serif',
        fontWeight: 400,
    },
    bulletPoint: {
        color: 'rgba(198, 203, 212, 1)',
        fontSize: { xs: '14px', sm: '18px', lg: '32px' },
        lineHeight: { xs: '160%', lg: '38px' },
        opacity: 0.9,
        fontFamily: '"Inter", sans-serif',
        fontWeight: 400,
        mb: 0,
        display: 'flex',
        alignItems: 'flex-start',
    },
    bulletPointText: {
        color: 'rgba(198, 203, 212, 1)',
        fontSize: { xs: '14px', sm: '18px', lg: '32px' },
        lineHeight: { xs: '160%', lg: '38px' },
        opacity: 0.9,
        fontFamily: '"Inter", sans-serif',
        fontWeight: 400,
        ml: 1,
        flex: 1,
    },
    contactInfo: {
        color: 'rgba(198, 203, 212, 1)',
        fontSize: { xs: '14px', sm: '18px', lg: '32px' },
        lineHeight: { xs: '160%', lg: '38px' },
        opacity: 0.9,
        fontFamily: '"Inter", sans-serif',
        fontWeight: 600,
        mt: 5,
    },
    bulletListContainer: {
        ml: 2
    }
};

// Privacy policy content data
const PRIVACY_CONTENT = {
    overview: "MoiPay, operated by Rubaai Technologies (Pvt) Ltd, respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our mobile app, merchant portal, and website.",

    sections: [
        {
            id: 'information-collection',
            title: '1. What Information We Collect',
            bulletPoints: [
                'Identity Data: Name, NIC/passport number, gender, date of birth',
                'Contact Data: Phone number and email address',
                'Financial Data: Bank account details and wallet balances',
                'Transactional Data: Bill payments, QR scans, recharges, donations, CRIB reports',
                'Device Data: OS, device ID'
            ]
        },
        {
            id: 'data-usage',
            title: '2. How We Use Your Data',
            bulletPoints: [
                'Facilitate financial transactions (wallet, QR, bills, donations, loans, taxation)',
                'Verify your identity (KYC, CRIB checks)',
                'Comply with legal obligations (e.g., Anti-Money Laundering)',
                'Offer personalized features and services',
                'Communicate updates and offers via SMS, push notifications, or email'
            ]
        },
        {
            id: 'data-sharing',
            title: '3. Data Sharing & Disclosure',
            description: 'We may share your data with:',
            bulletPoints: [
                'Licensed banks and LankaPay network (e.g., JustPay, CEFT)',
                'CRIB (Credit Information Bureau)',
                'Government and regulatory authorities (as required by law)',
                'Telecom operators for mobile recharges',
                'Third-party service providers under strict confidentiality'
            ],
            additionalText: 'We do not sell your data to advertisers or other third parties.'
        },
        {
            id: 'data-retention',
            title: '4. Data Retention & Security',
            bulletPoints: [
                'We retain your data as long as your account is active or as required by law',
                'Data is stored on secure cloud servers with encryption',
                'Access is restricted to authorized personnel under strict controls',
                'We follow the Sri Lanka Personal Data Protection Act, No. 9 of 2022'
            ]
        },
        {
            id: 'user-rights',
            title: '5. Your Rights',
            bulletPoints: [
                'Request access to your personal data',
                'Request corrections to incorrect data',
                'Request account/data deletion (subject to financial clearance)',
                'Withdraw consent for marketing at any time'
            ]
        },
        {
            id: 'children-privacy',
            title: '6. Children\'s Privacy',
            description: 'MoiPay is not intended for users under 18. We do not knowingly collect data from children.'
        },
        {
            id: 'policy-changes',
            title: '7. Changes to This Policy',
            description: 'Policy may be updated. The latest version is always available at: ',
            linkText: 'https://moipay.lk/privacy-policy',
            linkUrl: 'https://moipay.lk/privacy-policy'
        },
        {
            id: 'contact-us',
            title: '8. Contact Us',
            description: 'For questions, requests, or complaints:',
            contactInfo: {
                company: 'Rubaai Technologies (Pvt) Ltd',
                address: [
                    'No.08, Thiruvalluvar Supermarket',
                    'Bus Stand Building, Mannar Town, Sri Lanka'
                ],
                email: 'admin@rubaai.net'
            }
        }
    ]
};

/**
 * Renders a bullet point list item with consistent styling
 * @param {string} text - The text content for the bullet point
 * @returns {JSX.Element} The styled bullet point component
 */
const BulletPoint = ({ text }) => (
    <Box sx={STYLES.bulletPoint}>
        <Typography sx={{ fontSize: { xs: '20px', lg: '35px' }, lineHeight: 1, mt: 0.5 }}>•</Typography>
        <Typography sx={STYLES.bulletPointText}>
            {text}
        </Typography>
    </Box>
);

/**
 * Renders a section with title and bullet points
 * @param {Object} section - The section data containing title and bullet points
 * @returns {JSX.Element} The section component
 */
const PolicySection = ({ section }) => (
    <Box>
        <Typography variant="h4" sx={STYLES.sectionTitle}>
            {section.title}
        </Typography>

        {section.description && (
            <Typography sx={STYLES.bodyText}>
                {section.description}
                {section.linkText && (
                    <a 
                        href={section.linkUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                            color: '#4A90E2', 
                            textDecoration: 'underline',
                            wordBreak: 'break-all'
                        }}
                    >
                        {section.linkText}
                    </a>
                )}
            </Typography>
        )}

        {section.bulletPoints && (
            <Box sx={STYLES.bulletListContainer}>
                {section.bulletPoints.map((point, index) => (
                    <BulletPoint key={`${section.id}-point-${index}`} text={point} />
                ))}
            </Box>
        )}

        {section.additionalText && (
            <Typography sx={STYLES.bodyText} mb={2}>
                {section.additionalText}
            </Typography>
        )}

        {section.contactInfo && (
            <ContactInformation contactInfo={section.contactInfo} />
        )}
    </Box>
);

/**
 * Renders contact information section
 * @param {Object} contactInfo - The contact information data
 * @returns {JSX.Element} The contact information component
 */
const ContactInformation = ({ contactInfo }) => (
    <>
        <Typography sx={STYLES.contactInfo}>
            {contactInfo.company}
        </Typography>
        {contactInfo.address.map((line, index) => (
            <Typography key={`address-${index}`} sx={STYLES.bodyText}>
                {line}
            </Typography>
        ))}
        <Typography sx={STYLES.bodyText} mb={3} mt={3}>
            Email: {contactInfo.email}
        </Typography>
    </>
);

/**
 * Main Privacy Policy Screen Component
 * 
 * Displays the complete privacy policy for MoiPay with proper styling
 * and responsive design. The component is structured to be easily
 * maintainable and follows clean code principles.
 * 
 * @returns {JSX.Element} The privacy policy screen component
 */
const PrivacyScreen = () => {
    return (
        <Box sx={STYLES.container}>
            <PageContainer>
                <Box sx={STYLES.contentWrapper}>
                    {/* Main Title */}
                    <Typography variant="h2" sx={STYLES.mainTitle}>
                        MoiPay Privacy Policy
                    </Typography>

                    {/* Content Section */}
                    <Box sx={STYLES.contentSection}>
                        <Stack spacing={3} sx={STYLES.stackContainer}>
                            {/* Overview Section */}
                            <Typography sx={STYLES.bodyText}>
                                {PRIVACY_CONTENT.overview}
                            </Typography>

                            {/* Render all policy sections */}
                            {PRIVACY_CONTENT.sections.map((section) => (
                                <PolicySection key={section.id} section={section} />
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </PageContainer>
        </Box>
    );
};

export default PrivacyScreen; 